/**
 * Strategies Page - AI-Powered Strategy Lab
 * Version: 6.2.0
 */
import { useState, useEffect, useCallback } from 'react'
import './Strategies.css'
import WalkForwardPanel from '../components/WalkForwardPanel'
import PullToRefresh from '../components/PullToRefresh'
import InfiniteScroll from '../components/InfiniteScroll'
import SwipeableRow from '../components/SwipeableRow'
import PageTransition from '../components/PageTransition'
import EmptyState from '../components/EmptyState'
import SearchInput from '../components/SearchInput'
import FilterDropdown from '../components/FilterDropdown'
import FilterChips from '../components/FilterChips'
import Modal from '../components/Modal'
import ConfirmDialog from '../components/ConfirmDialog'
import BottomSheet from '../components/BottomSheet'
import PageHeader from '../components/PageHeader'
import AnimateOnScroll from '../components/AnimateOnScroll'
import LoadingSpinner from '../components/LoadingSpinner'
import PerformanceMetrics from '../components/PerformanceMetrics'
import StrategyBuilder from '../components/StrategyBuilder'
import PublishWizard from '../components/PublishWizard'
import { TrendingUp, TrendingDown, GitBranch, Calculator, Layers } from 'lucide-react'

const PRE_BUILT_STRATEGIES = [
  { 
    id: 'rsi-reversal', 
    name: 'RSI Reversal', 
    icon: '📉', 
    description: 'Mean reversion on RSI oversold/overbought zones',
    winRate: '68%',
    trades: '1.2K',
    category: 'Mean Reversion'
  },
  { 
    id: 'macd-crossover', 
    name: 'MACD Crossover', 
    icon: '📊', 
    description: 'Trend following with MACD signal line crossovers',
    winRate: '72%',
    trades: '890',
    category: 'Trend Following'
  },
  { 
    id: 'ema-crossover', 
    name: 'EMA Crossover', 
    icon: '📈', 
    description: 'Multi-EMA crossover for trend detection',
    winRate: '75%',
    trades: '2.1K',
    category: 'Trend Following'
  },
  { 
    id: 'bollinger-bounce', 
    name: 'Bollinger Bounce', 
    icon: '🔄', 
    description: 'Range trading on Bollinger Band touches',
    winRate: '64%',
    trades: '980',
    category: 'Range Trading'
  },
  { 
    id: 'supertrend', 
    name: 'Supertrend Reversal', 
    icon: '⚡', 
    description: 'Supertrend-based trend reversal signals',
    winRate: '71%',
    trades: '756',
    category: 'Trend Reversal'
  },
  { 
    id: 'atr-volatility', 
    name: 'ATR Volatility', 
    icon: '🌊', 
    description: 'Volatility breakout using ATR bands',
    winRate: '69%',
    trades: '543',
    category: 'Volatility'
  },
  { 
    id: 'volume-spike', 
    name: 'Volume Spike', 
    icon: '📊', 
    description: 'Volume-based breakout detection',
    winRate: '66%',
    trades: '432',
    category: 'Volume'
  },
  { 
    id: 'moving-average', 
    name: 'MA Envelope', 
    icon: '✉️', 
    description: 'Moving average envelope breakout',
    winRate: '63%',
    trades: '321',
    category: 'Breakout'
  }
]

const CUSTOM_STRATEGIES = [
  { id: 'custom-1', name: 'My RSI Strategy', created: '2 days ago', status: 'active' },
  { id: 'custom-2', name: 'MACD + Volume', created: '1 week ago', status: 'draft' },
]

// Multi-Leg Options Strategies
const OPTIONS_STRATEGIES = [
  {
    id: 'bull-call-spread',
    name: 'Bull Call Spread',
    description: 'Buy lower strike Call, Sell higher strike Call',
    icon: <TrendingUp size={20} />,
  },
  {
    id: 'bear-put-spread',
    name: 'Bear Put Spread',
    description: 'Buy higher strike Put, Sell lower strike Put',
    icon: <TrendingDown size={20} />,
  },
  {
    id: 'iron-condor',
    name: 'Iron Condor',
    description: 'Sell OTM Put Spread + Sell OTM Call Spread',
    icon: <Layers size={20} />,
  },
  {
    id: 'straddle',
    name: 'Straddle',
    description: 'Buy ATM Call + Buy ATM Put (Same Strike)',
    icon: <GitBranch size={20} />,
  },
  {
    id: 'strangle',
    name: 'Strangle',
    description: 'Buy OTM Call + Buy OTM Put',
    icon: <GitBranch size={20} />,
  },
  {
    id: 'butterfly',
    name: 'Butterfly Spread',
    description: 'Buy 1 lower, Sell 2 middle, Buy 1 higher',
    icon: <Layers size={20} />,
  },
]

function Strategies() {
  const [activeTab, setActiveTab] = useState('prebuilt')
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [visibleStrategies, setVisibleStrategies] = useState(PRE_BUILT_STRATEGIES.slice(0, 4))
  const [hasMore, setHasMore] = useState(true)
  
  // Search and Filter State
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedWinRate, setSelectedWinRate] = useState<string[]>([])
  
  // Modal State
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [strategyToDelete, setStrategyToDelete] = useState<string | null>(null)
  const [showStrategyDetail, setShowStrategyDetail] = useState(false)
  const [showQuickCreate, setShowQuickCreate] = useState(false)
  const [showPublishWizard, setShowPublishWizard] = useState(false)
  // Options Builder State
  const [selectedOptionsStrategy, setSelectedOptionsStrategy] = useState<string>('bull-call-spread')
  const [optionsLegs, setOptionsLegs] = useState<any[]>([])
  const [optionsLtp, setOptionsLtp] = useState(24456)
  const [optionsPnlTable, setOptionsPnlTable] = useState<{ price: number; pnl: number }[]>([])
  
  // Filter Options
  const categoryOptions = [
    { id: 'mean-reversion', label: 'Mean Reversion', icon: '📉' },
    { id: 'trend-following', label: 'Trend Following', icon: '📈' },
    { id: 'range-trading', label: 'Range Trading', icon: '🔄' },
    { id: 'volatility', label: 'Volatility', icon: '🌊' },
    { id: 'breakout', label: 'Breakout', icon: '💥' },
  ]
  
  const winRateOptions = [
    { id: 'high', label: 'High (70%+)', count: 3 },
    { id: 'medium', label: 'Medium (60-70%)', count: 4 },
    { id: 'low', label: 'Low (<60%)', count: 1 },
  ]

  // Simulated refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true)
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500))
    setVisibleStrategies(PRE_BUILT_STRATEGIES.slice(0, 4))
    setHasMore(true)
    setRefreshing(false)
  }, [])

  // Load more strategies
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return
    
    setLoadingMore(true)
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800))
    
    const currentLength = visibleStrategies.length
    const nextStrategies = PRE_BUILT_STRATEGIES.slice(currentLength, currentLength + 4)
    
    if (nextStrategies.length > 0) {
      setVisibleStrategies(prev => [...prev, ...nextStrategies])
      if (currentLength + nextStrategies.length >= PRE_BUILT_STRATEGIES.length) {
        setHasMore(false)
      }
    } else {
      setHasMore(false)
    }
    
    setLoadingMore(false)
  }, [loadingMore, hasMore, visibleStrategies])

  useEffect(() => {
    if (selectedStrategy) {
      setAnalyzing(true)
      const timer = setTimeout(() => setAnalyzing(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [selectedStrategy])

  // Generate legs when options strategy or LTP changes
  useEffect(() => {
    const baseStrike = Math.round(optionsLtp / 100) * 100
    
    const generateLegs = () => {
      switch (selectedOptionsStrategy) {
        case 'bull-call-spread':
          return [
            { type: 'call', action: 'buy', strike: baseStrike - 100, premium: 80, quantity: 1 },
            { type: 'call', action: 'sell', strike: baseStrike + 100, premium: 60, quantity: 1 },
          ]
        case 'bear-put-spread':
          return [
            { type: 'put', action: 'buy', strike: baseStrike + 100, premium: 100, quantity: 1 },
            { type: 'put', action: 'sell', strike: baseStrike - 100, premium: 70, quantity: 1 },
          ]
        case 'iron-condor':
          return [
            { type: 'put', action: 'buy', strike: baseStrike - 300, premium: 30, quantity: 1 },
            { type: 'put', action: 'sell', strike: baseStrike - 100, premium: 60, quantity: 1 },
            { type: 'call', action: 'sell', strike: baseStrike + 100, premium: 70, quantity: 1 },
            { type: 'call', action: 'buy', strike: baseStrike + 300, premium: 40, quantity: 1 },
          ]
        case 'straddle':
          return [
            { type: 'call', action: 'buy', strike: baseStrike, premium: 120, quantity: 1 },
            { type: 'put', action: 'buy', strike: baseStrike, premium: 110, quantity: 1 },
          ]
        case 'strangle':
          return [
            { type: 'call', action: 'buy', strike: baseStrike + 100, premium: 80, quantity: 1 },
            { type: 'put', action: 'buy', strike: baseStrike - 100, premium: 90, quantity: 1 },
          ]
        case 'butterfly':
          return [
            { type: 'call', action: 'buy', strike: baseStrike - 200, premium: 150, quantity: 1 },
            { type: 'call', action: 'sell', strike: baseStrike, premium: 100, quantity: 2 },
            { type: 'call', action: 'buy', strike: baseStrike + 200, premium: 60, quantity: 1 },
          ]
        default:
          return []
      }
    }

    const legs = generateLegs()
    setOptionsLegs(legs)

    // Generate P&L table
    const pnlTable: { price: number; pnl: number }[] = []
    for (let price = optionsLtp - 500; price <= optionsLtp + 500; price += 25) {
      let pnl = 0
      legs.forEach(leg => {
        let intrinsic = 0
        if (leg.type === 'call') {
          intrinsic = Math.max(0, price - leg.strike)
        } else {
          intrinsic = Math.max(0, leg.strike - price)
        }
        const legPnl = (intrinsic - leg.premium) * leg.quantity * 75
        pnl += leg.action === 'buy' ? legPnl : -legPnl
      })
      pnlTable.push({ price, pnl })
    }
    setOptionsPnlTable(pnlTable)
  }, [selectedOptionsStrategy, optionsLtp])

  const tabs = [
    { id: 'prebuilt', label: 'Pre-built', icon: '🚀', count: 8 },
    { id: 'custom', label: 'My Strategies', icon: '✨', count: 2 },
    { id: 'backtest', label: 'Backtesting', icon: '📊', count: null },
    { id: 'walkforward', label: 'Walk-Forward', icon: '🔬', count: null },
    { id: 'builder', label: 'AI Builder', icon: '🧠', count: null },
    { id: 'options', label: 'Options Builder', icon: '📈', count: null },
  ]

  return (
    <div className="strategies-page">
      {/* Page Header */}
      <PageHeader
        title="Strategy Builder"
        subtitle="Build, test, and deploy trading strategies with AI assistance"
        icon="🧠"
        badge="AI Powered"
        badgeColor="primary"
        breadcrumbs={[
          { label: 'Dashboard', path: '/' },
          { label: 'Strategy Lab' }
        ]}
        actions={[
          { label: 'Create Strategy', icon: '➕', onClick: () => setShowQuickCreate(true), variant: 'primary' }
        ]}
      />

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-number">22</span>
          <span className="stat-label">Total Strategies</span>
        </div>
        <div className="stat-divider" />
        <div className="stat-item">
          <span className="stat-number">8</span>
          <span className="stat-label">Pre-built</span>
        </div>
        <div className="stat-divider" />
        <div className="stat-item">
          <span className="stat-number">14</span>
          <span className="stat-label">Custom</span>
        </div>
        <div className="stat-divider" />
        <div className="stat-item">
          <span className="stat-number">5</span>
          <span className="stat-label">Active</span>
        </div>
        <div className="stat-divider" />
        <div className="stat-item">
          <span className="stat-number">70%</span>
          <span className="stat-label">Avg Win Rate</span>
        </div>
      </div>

      {/* Performance Metrics */}
      <AnimateOnScroll animation="fadeInUp">
        <div className="section-wrapper">
          <PerformanceMetrics
            metrics={[
              { label: 'Win Rate', value: '68.5%', change: 2.3, status: 'positive', icon: '🎯' },
              { label: 'Sharpe Ratio', value: '2.14', change: 0.15, status: 'positive', icon: '📈' },
              { label: 'Total Trades', value: '156', icon: '🔢' },
              { label: 'Profit Factor', value: '2.35', change: 0.08, status: 'positive', icon: '💹' },
            ]}
            variant="grid"
          />
        </div>
      </AnimateOnScroll>

      {/* Tabs */}
      <div className="strategy-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`strategy-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
            {tab.count !== null && <span className="tab-count">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* Search and Filter Bar */}
      {activeTab === 'prebuilt' && (
        <div className="search-filter-bar">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search strategies..."
            size="medium"
          />
          <div className="filter-dropdowns">
            <FilterDropdown
              label="Category"
              options={categoryOptions}
              selected={selectedCategories}
              onChange={setSelectedCategories}
              multiple
              searchable
            />
            <FilterDropdown
              label="Win Rate"
              options={winRateOptions}
              selected={selectedWinRate}
              onChange={setSelectedWinRate}
              multiple
            />
          </div>
        </div>
      )}

      {/* Active Filters */}
      {(selectedCategories.length > 0 || selectedWinRate.length > 0) && (
        <FilterChips
          chips={[
            ...selectedCategories.map(id => ({
              id,
              label: categoryOptions.find(o => o.id === id)?.label || id,
              icon: categoryOptions.find(o => o.id === id)?.icon
            })),
            ...selectedWinRate.map(id => ({
              id,
              label: winRateOptions.find(o => o.id === id)?.label || id
            }))
          ]}
          onRemove={(id) => {
            if (selectedCategories.includes(id)) {
              setSelectedCategories(prev => prev.filter(c => c !== id))
            } else {
              setSelectedWinRate(prev => prev.filter(w => w !== id))
            }
          }}
          onClearAll={() => {
            setSelectedCategories([])
            setSelectedWinRate([])
          }}
        />
      )}

      {/* Pre-built Strategies */}
      {activeTab === 'prebuilt' && (
        <PullToRefresh onRefresh={handleRefresh}>
          <InfiniteScroll
            hasMore={hasMore}
            isLoading={loadingMore}
            onLoadMore={handleLoadMore}
            endMessage={
              <div className="end-message">
                <span className="end-message-icon">✓</span>
                <span>You've seen all strategies</span>
              </div>
            }
          >
            <div className="strategy-grid">
              {visibleStrategies.map((strategy, index) => (
                <div 
                  key={strategy.id}
                  className={`strategy-card ${selectedStrategy === strategy.id ? 'selected' : ''}`}
                  onClick={() => setSelectedStrategy(strategy.id)}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="card-glow" />
                  <div className="strategy-header">
                    <div className="strategy-icon">{strategy.icon}</div>
                    <span className="strategy-category">{strategy.category}</span>
                  </div>
                  <h3 className="strategy-name">{strategy.name}</h3>
                  <p className="strategy-desc">{strategy.description}</p>
                  <div className="strategy-metrics">
                    <div className="metric">
                      <span className="metric-value success">{strategy.winRate}</span>
                      <span className="metric-label">Win Rate</span>
                    </div>
                    <div className="metric">
                      <span className="metric-value">{strategy.trades}</span>
                      <span className="metric-label">Trades</span>
                    </div>
                  </div>
                  <div className="strategy-footer">
                    <button className="use-btn">Use Strategy</button>
                    <button className="backtest-btn">Backtest</button>
                  </div>
                  <div className="card-shine" />
                </div>
              ))}
            </div>
          </InfiniteScroll>
        </PullToRefresh>
      )}

      {/* Custom Strategies */}
      {activeTab === 'custom' && (
        <PageTransition type="slide">
          <div className="custom-section">
            <div className="custom-header">
              <h2>Your Strategies</h2>
              <button 
                className="btn btn-primary"
                onClick={() => setShowPublishWizard(true)}
              >
                <span>🚀</span> Publish Strategy
              </button>
            </div>
            <div className="custom-list">
              {CUSTOM_STRATEGIES.length === 0 ? (
                <EmptyState
                  title="No Custom Strategies Yet"
                  description="Create your first custom trading strategy using our AI-powered builder."
                  illustration="strategy"
                  action={{
                    label: 'Create Strategy',
                    onClick: () => console.log('Create new strategy'),
                    icon: '✨'
                  }}
                  secondaryAction={{
                    label: 'Use Pre-built',
                    onClick: () => setActiveTab('prebuilt')
                  }}
                />
              ) : (
                CUSTOM_STRATEGIES.map((strategy) => (
                  <SwipeableRow
                    key={strategy.id}
                    rightActions={[
                      {
                        id: 'edit',
                        icon: '✏️',
                        label: 'Edit',
                        color: 'var(--primary)',
                        onClick: () => console.log('Edit:', strategy.id)
                      },
                      {
                        id: 'delete',
                        icon: '🗑️',
                        label: 'Delete',
                        color: 'var(--danger)',
                        onClick: () => console.log('Delete:', strategy.id)
                      }
                    ]}
                  >
                    <div className="custom-card">
                      <div className="custom-icon">📝</div>
                      <div className="custom-info">
                        <h4>{strategy.name}</h4>
                        <span className="custom-date">Created {strategy.created}</span>
                      </div>
                      <span className={`custom-status ${strategy.status}`}>
                        {strategy.status}
                      </span>
                    </div>
                  </SwipeableRow>
                ))
              )}
            </div>
            {CUSTOM_STRATEGIES.length > 0 && (
              <div className="ai-suggestion card">
                <div className="ai-header">
                  <span className="ai-icon">💡</span>
                  <span>AI Suggestion</span>
                </div>
                <p>Based on your trading history, consider trying a combination of RSI Reversal with Volume confirmation for better entry timing.</p>
                <button className="btn btn-secondary">Apply Suggestion</button>
              </div>
            )}
          </div>
        </PageTransition>
      )}

      {/* Backtesting */}
      {activeTab === 'backtest' && (
        <div className="backtest-section">
          <div className="backtest-config card">
            <h3>📊 Backtest Configuration</h3>
            <div className="config-grid">
              <div className="config-item">
                <label>Strategy</label>
                <select defaultValue="rsi-reversal">
                  {PRE_BUILT_STRATEGIES.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div className="config-item">
                <label>Timeframe</label>
                <select defaultValue="5min">
                  <option value="1min">1 Minute</option>
                  <option value="5min">5 Minutes</option>
                  <option value="15min">15 Minutes</option>
                  <option value="1hour">1 Hour</option>
                  <option value="1day">1 Day</option>
                </select>
              </div>
              <div className="config-item">
                <label>Date Range</label>
                <select defaultValue="90d">
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                  <option value="6m">Last 6 Months</option>
                  <option value="1y">Last 1 Year</option>
                </select>
              </div>
              <div className="config-item">
                <label>Initial Capital</label>
                <input type="text" defaultValue="$100,000" />
              </div>
            </div>
            <button className="btn btn-primary run-btn">
              {analyzing ? 'Analyzing...' : '▶ Run Backtest'}
            </button>
          </div>

          {analyzing && (
            <div className="backtest-progress card">
              <div className="progress-header">
                <span className="ai-badge">✦ AI Processing</span>
                <span>Running simulation...</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" />
              </div>
              <div className="progress-stats">
                <span>Simulated: 1,247 trades</span>
                <span>ETA: 12 seconds</span>
              </div>
            </div>
          )}

          <div className="backtest-results card">
            <h3>Results Summary</h3>
            <div className="results-grid">
              <div className="result-item">
                <span className="result-label">Total Return</span>
                <span className="result-value success">+24.5%</span>
              </div>
              <div className="result-item">
                <span className="result-label">Win Rate</span>
                <span className="result-value">72%</span>
              </div>
              <div className="result-item">
                <span className="result-label">Sharpe Ratio</span>
                <span className="result-value">1.85</span>
              </div>
              <div className="result-item">
                <span className="result-label">Max Drawdown</span>
                <span className="result-value warning">-8.2%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Walk-Forward Testing */}
      {activeTab === 'walkforward' && (
        <WalkForwardPanel />
      )}

      {/* AI Builder */}
      {activeTab === 'builder' && (
        <StrategyBuilder />
      )}

      {/* Options Builder */}
      {activeTab === 'options' && (
        <div className="options-builder-section">
          <div className="options-builder-header">
            <h2><GitBranch size={20} /> Multi-Leg Options Builder</h2>
            <p>Build and analyze options spread strategies</p>
          </div>
          
          {/* LTP Input */}
          <div className="ltp-input-group">
            <label>Current Price (LTP):</label>
            <input
              type="number"
              value={optionsLtp}
              onChange={(e) => setOptionsLtp(Number(e.target.value))}
              className="ltp-input"
            />
          </div>

          {/* Strategy Grid */}
          <div className="options-strategy-grid">
            {OPTIONS_STRATEGIES.map(strategy => (
              <div
                key={strategy.id}
                className={`options-strategy-card ${selectedOptionsStrategy === strategy.id ? 'selected' : ''}`}
                onClick={() => setSelectedOptionsStrategy(strategy.id)}
              >
                <div className="strategy-icon">{strategy.icon}</div>
                <div className="strategy-info">
                  <div className="strategy-name">{strategy.name}</div>
                  <div className="strategy-desc">{strategy.description}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Legs Configuration */}
          <div className="legs-configuration">
            <h3><Calculator size={18} /> Leg Configuration</h3>
            <div className="legs-table">
              <div className="legs-header">
                <span>Leg</span>
                <span>Type</span>
                <span>Action</span>
                <span>Strike</span>
                <span>Premium</span>
                <span>Qty</span>
              </div>
              {optionsLegs.length > 0 ? optionsLegs.map((leg, idx) => (
                <div key={idx} className="leg-row">
                  <span className="leg-num">Leg {idx + 1}</span>
                  <select value={leg.type} disabled>
                    <option value="call">CALL</option>
                    <option value="put">PUT</option>
                  </select>
                  <select value={leg.action} disabled>
                    <option value="buy">BUY</option>
                    <option value="sell">SELL</option>
                  </select>
                  <input type="number" value={leg.strike} readOnly />
                  <input type="number" value={leg.premium} readOnly />
                  <input type="number" value={leg.quantity} readOnly />
                </div>
              )) : (
                <div className="no-legs-message">
                  Select a strategy to configure legs
                </div>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="options-summary">
            <div className="summary-card profit">
              <span>Max Profit</span>
              <strong>₹{optionsPnlTable.length ? Math.max(...optionsPnlTable.map(t => t.pnl)).toLocaleString() : '0'}</strong>
            </div>
            <div className="summary-card loss">
              <span>Max Loss</span>
              <strong>₹{optionsPnlTable.length ? Math.abs(Math.min(...optionsPnlTable.map(t => t.pnl))).toLocaleString() : '0'}</strong>
            </div>
            <div className="summary-card breakeven">
              <span>Breakeven</span>
              <strong>{optionsLtp.toLocaleString()}</strong>
            </div>
          </div>
        </div>
      )}

      {/* Strategy Performance */}
      <section className="performance-section card">
        <div className="section-header">
          <h2 className="section-title">📈 Strategy Performance</h2>
          <span className="live-badge">
            <span className="live-dot" />
            LIVE
          </span>
        </div>
        <div className="performance-chart">
          <div className="chart-placeholder">
            <div className="chart-line" />
            <div className="chart-bars">
              {[40, 65, 45, 80, 55, 90, 70, 85, 95, 75, 88, 92].map((h, i) => (
                <div key={i} className="chart-bar" style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
        </div>
        <div className="performance-legend">
          <div className="legend-item">
            <span className="legend-color success" />
            <span>Winning Trades</span>
          </div>
          <div className="legend-item">
            <span className="legend-color danger" />
            <span>Losing Trades</span>
          </div>
        </div>
      </section>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false)
          setStrategyToDelete(null)
        }}
        onConfirm={() => {
          // Handle delete logic here
          console.log('Deleting strategy:', strategyToDelete)
          setShowDeleteConfirm(false)
          setStrategyToDelete(null)
        }}
        title="Delete Strategy"
        message="Are you sure you want to delete this strategy? This action cannot be undone."
        confirmText="Delete"
        cancelText="Keep"
        type="danger"
      />

      {/* Strategy Detail Modal */}
      <Modal
        isOpen={showStrategyDetail}
        onClose={() => setShowStrategyDetail(false)}
        title="Strategy Details"
        size="large"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowStrategyDetail(false)}>
              Close
            </button>
            <button className="btn btn-primary" onClick={() => setShowStrategyDetail(false)}>
              Backtest Strategy
            </button>
          </>
        }
      >
        <div className="strategy-detail-content">
          <p>Strategy details and configuration options will appear here.</p>
        </div>
      </Modal>

      {/* Quick Create Bottom Sheet */}
      <BottomSheet
        isOpen={showQuickCreate}
        onClose={() => setShowQuickCreate(false)}
        title="Create New Strategy"
        snapPoints={[50, 90]}
      >
        <div className="quick-create-content">
          <p>Quick strategy creation form will appear here.</p>
          <button className="btn btn-primary" style={{ width: '100%', marginTop: '16px' }}>
            Create Strategy
          </button>
        </div>
      </BottomSheet>

      {/* Publish Wizard */}
      <PublishWizard
        isOpen={showPublishWizard}
        onClose={() => setShowPublishWizard(false)}
        onSuccess={(strategyId) => {
          console.log('Strategy published:', strategyId)
        }}
      />
    </div>
  )
}

export default Strategies